import React, { Component, useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFeathers, isPermitted } from '../../app/util';
import { Link, Redirect, useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import { mergeElectionsByKey } from '../../app/actions';
import {
  CTAButton,
  Modal,
  LoadingSpinner,
  ElectionPreviewTile,
  SearchTextField,
  StateFilterButton,
  ElectionsCalendarView,
  Title,
} from '../../components';
import CreateElection from '../CreateElection';
import styled from 'styled-components';
import { Tabs, Tab, Pagination, InputAdornment, Button, Snackbar, Alert, ToggleButtonGroup, ToggleButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import moment from 'moment';

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;

  h1 {
    font-size: 28px;
    margin: 0;
  }

  h2 {
    font-size: 18px;
    opacity: 0.6;
    letter-spacing: 2px;
    margin: 0;
  }
`

const LoadingTile = styled.div`
  background-color: #FFFFFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;

  span {
    marginBottom: 20px;
  }
`;

const ElectionTileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  margin-bottom: 30px;
`;

const TabsWrapper = styled.div`
  margin: 20px 0;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px 0;
`;

const CalendarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 400px;
  flex: 1;
`;

const TAB_VALUES = ['live', 'previous'];
const VIEW_VALUES = ['list', 'calendar'];
const CALENDAR_PAGE_SIZE = 20;

const getDefaultCalendarDateWindow = () => ({
  start: moment.utc().subtract(1, 'month').startOf('day').toDate(),
  end: moment.utc().add(1, 'year').endOf('day').toDate()
});

const loadAllCalendarPages = async (feathers, serviceName, query) => {
  const results = [];
  let skip = 0;
  let hasMore = true;

  while (hasMore) {
    const response = await feathers.getService(serviceName).find({
      query: {
        ...query,
        $limit: CALENDAR_PAGE_SIZE,
        $skip: skip
      }
    });
    const page = response.data || [];
    if (page.length === 0) break;

    results.push(...page);
    skip += page.length;
    hasMore = response.total == null
      ? page.length === CALENDAR_PAGE_SIZE
      : skip < response.total;
  }

  return results;
}

const loadProjectDatesForCalendarElections = async (feathers, dateWindow, stateQuery) => {
  return loadAllCalendarPages(feathers, 'project-dates', {
    date: { $gte: dateWindow.start, $lte: dateWindow.end },
    $sort: { date: 1 },
    ...stateQuery
  });
}

const loadOrganizationNamesForProjectDates = async (feathers, projectDates) => {
  const orgKeys = [...new Set(projectDates.map(projectDate => projectDate.organization).filter(Boolean))];
  const orgEntries = await Promise.all(
    orgKeys.map(async (orgKey) => {
      try {
        const org = await feathers.getService('organizations').get(orgKey);
        return [orgKey, org?.name || orgKey];
      } catch {
        return [orgKey, orgKey];
      }
    })
  );
  return Object.fromEntries(orgEntries);
}

const Elections = () => {
  const feathers = useFeathers();
  const route = useRouteMatch();
  const history = useHistory();
  const location = useLocation();

  const dispatch = useDispatch();
  const user = useSelector(state => state.user);

  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const viewParam = searchParams.get('view');
  const activeTab = TAB_VALUES.includes(tabParam) ? TAB_VALUES.indexOf(tabParam) : 0;
  const viewMode = VIEW_VALUES.includes(viewParam) ? viewParam : 'list';

  const setUrlParams = useCallback((updates) => {
    const params = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([k, v]) => {
      if (v != null) params.set(k, v);
      else params.delete(k);
    });
    const search = params.toString();
    history.push({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [location, history]);

  const [ error, setError ] = useState(null);
  const [ loading, setLoading ] = useState(false);
  const [ initialLoad, setInitialLoad ] = useState(false);
  const [ currentPage, setCurrentPage ] = useState(1);
  const [ liveElections, setLiveElections ] = useState([]);
  const [ previousElections, setPreviousElections ] = useState([]);
  const [ liveElectionsTotal, setLiveElectionsTotal ] = useState(0);
  const [ previousElectionsTotal, setPreviousElectionsTotal ] = useState(0);
  const [ searchTerm, setSearchTerm ] = useState('');
  const [ debouncedSearchTerm, setDebouncedSearchTerm ] = useState('');
  const [ precedentElectionsMap, setPrecedentElectionsMap ] = useState({});
  const searchInputRef = useRef(null);
  const modalOpen = route.path === '/elections/new';

  const [ states, setStates ] = useState([]);
  const [ stateFilterId, setStateFilterId ] = useState(null);
  const [ calendarElections, setCalendarElections ] = useState([]);
  const [ calendarProjectDates, setCalendarProjectDates ] = useState([]);
  const [ calendarProjectDateOrgNames, setCalendarProjectDateOrgNames ] = useState({});
  const [ calendarLoading, setCalendarLoading ] = useState(false);

  // useEffect(() => {
  //   if(feathers) getElections()
  // }, [ feathers ])

  // useEffect(() => {
  //   if(!modalOpen) getElections()
  // }, [ modalOpen ])

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle Ctrl+F / Cmd+F to focus search
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if(feathers) {
      loadElections();
    }
  }, [ feathers, currentPage, activeTab, debouncedSearchTerm ])

  // Load states for state filter (calendar view)
  useEffect(() => {
    if (!feathers) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await feathers.getService('districts').find({
          query: { type: 'state', $sort: { name: 1 }, $limit: 100 }
        });
        if (!cancelled) setStates(res.data || []);
      } catch (err) {
        console.error('Error fetching states for filter:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [ feathers ]);

  const loadCalendarElections = useCallback(async () => {
    if (!feathers || viewMode !== 'calendar') return;
    setCalendarLoading(true);
    try {
      const dateWindow = getDefaultCalendarDateWindow();
      const stateQuery = stateFilterId
        ? { state: stateFilterId }
        : user.limitAccessToStates
          ? { state: user.limitAccessToStates }
          : {};
      const electionQuery = {
        test: { $ne: true },
        date: { $gte: dateWindow.start, $lte: dateWindow.end },
        $sort: { date: 1 },
        $includeElectionImplementation: true,
        ...stateQuery
      };

      const [ nextCalendarElections, projectDates ] = await Promise.all([
        loadAllCalendarPages(feathers, 'elections', electionQuery),
        loadProjectDatesForCalendarElections(feathers, dateWindow, stateQuery)
      ]);
      const orgNamesByKey = await loadOrganizationNamesForProjectDates(feathers, projectDates);
      setCalendarElections(nextCalendarElections);
      setCalendarProjectDates(projectDates);
      setCalendarProjectDateOrgNames(orgNamesByKey);
      dispatch(mergeElectionsByKey(nextCalendarElections));
    } catch (err) {
      console.error('Error loading calendar elections:', err);
      setCalendarElections([]);
      setCalendarProjectDates([]);
      setCalendarProjectDateOrgNames({});
    } finally {
      setCalendarLoading(false);
    }
  }, [ feathers, viewMode, stateFilterId, user.limitAccessToStates, dispatch ]);

  useEffect(() => {
    if (viewMode === 'calendar' && feathers) {
      loadCalendarElections();
    }
  }, [ viewMode, feathers, loadCalendarElections ]);

  const loadElections = useCallback(
    async () => {
      if(modalOpen || loading) return;
      setLoading(true)
      setError(null);

      try {
        const isLiveTab = activeTab === 0;
        const statusFilter = isLiveTab 
          ? ['prep', 'live', 'results-counting'] 
          : ['results-certified', 'results-tentative', 'results-complete'];
        const $sort = isLiveTab ? { date: 1 } : { date: -1 };

        const query = {
          $limit: 10,
          $skip: (currentPage - 1) * 10,
          test: { $ne: true },
          status: { $in: statusFilter },
          $sort,
          ...(user.limitAccessToStates ? { state: user.limitAccessToStates } : {})
        };

        // Add search functionality
        if (debouncedSearchTerm.trim()) {
          query.name = { $search: debouncedSearchTerm.trim() };
        }

        const res = await feathers.getService('elections').find({ query })

        if (isLiveTab) {
          setLiveElections(res.data);
          setLiveElectionsTotal(res.total);
        } else {
          setPreviousElections(res.data);
          setPreviousElectionsTotal(res.total);
        }

        // Cache fetched elections in redux byKey for use elsewhere in the app
        dispatch(mergeElectionsByKey(res.data));

        // Fetch precedent elections for runoff elections
        const runoffElections = res.data.filter(e => 
          (e.electionType === 'primary-runoff' || e.electionType === 'general-runoff') && e.runoffPrecedent
        );
        
        if (runoffElections.length > 0) {
          const precedentKeys = [...new Set(runoffElections.map(e => e.runoffPrecedent).filter(Boolean))];
          try {
            const precedentRes = await feathers.getService('elections').find({
              query: {
                key: precedentKeys,
                $limit: precedentKeys.length
              }
            });
            
            const precedentMap = {};
            precedentRes.data.forEach(election => {
              precedentMap[election.key] = election;
            });
            
            setPrecedentElectionsMap(prev => ({ ...prev, ...precedentMap }));
            dispatch(mergeElectionsByKey(precedentRes.data));
          } catch (error) {
            console.error('Error fetching precedent elections:', error);
          }
        }

        setInitialLoad(true);
      } catch(error) {
        console.log(error)
        setError(error)
      } finally {
        setLoading(false)
      }
    },
    [ modalOpen, loading, feathers, currentPage, user, activeTab, debouncedSearchTerm ]
  )

  const handleTabChange = (event, newValue) => {
    setUrlParams({ tab: TAB_VALUES[newValue] });
    setCurrentPage(1); // Reset to first page when switching tabs
  }

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  }

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  }

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  }

  return (
    <Wrapper>
      <Title>Elections</Title>
      {
        modalOpen &&
        (
          <Modal onClose={() => history.goBack()}>
            <CreateElection
              onClose={() => history.goBack()}
              onSuccess={(election) => {
                history.push(`/elections/${election.key}/setup`);
              }}
            />
          </Modal>
        )
      }
      <Row>
        <h1>Elections</h1>
        {
          isPermitted(user, ['editor', 'super-admin'])
          ? <Button 
              variant="contained" 
              onClick={() => history.push('/elections/new')}
            >
              New election
            </Button>
          : <div/>
        }
      </Row>
      
      <TabsWrapper>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Live Elections" />
          <Tab label="Previous Elections" />
        </Tabs>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <div style={{ marginLeft: 8 }}>
            <StateFilterButton
              label="State"
              value={stateFilterId}
              onChange={setStateFilterId}
              states={states}
            />
          </div>
          {viewMode === 'list' && (
            <SearchTextField
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              inputRef={searchInputRef}
              InputProps={{
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <ClearIcon 
                      style={{ cursor: 'pointer' }}
                      onClick={handleClearSearch}
                    />
                  </InputAdornment>
                ),
              }}
            />
          )}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val != null && setUrlParams({ view: val })}
            size="small"
            sx={{ ml: 1 }}
          >
            <ToggleButton value="list" aria-label="List view">
              <ViewListIcon sx={{ mr: 0.5 }} />
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="Calendar view">
              <CalendarMonthIcon sx={{ mr: 0.5 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </TabsWrapper>

      {viewMode === 'calendar' ? (
        <CalendarWrapper>
          {calendarLoading ? (
            <LoadingTile>
              <LoadingSpinner scale={0.8}/>
            </LoadingTile>
          ) : (
            <ElectionsCalendarView
              elections={calendarElections}
              stateFilterId={stateFilterId}
              projectDates={calendarProjectDates}
              orgNamesByKey={calendarProjectDateOrgNames}
            />
          )}
        </CalendarWrapper>
      ) : (
        <>
          <ElectionTileWrapper>
            {
              (loading || !initialLoad)
              ?
                <LoadingTile>
                  <LoadingSpinner scale={0.8}/>
                </LoadingTile>
              :
                (activeTab === 0 ? liveElections : previousElections).length > 0
                ?
                  (activeTab === 0 ? liveElections : previousElections).map((election, idx) => (
                    <ElectionPreviewTile
                      key={election.key}
                      onClick={{ to: `/elections/${election.key}` }}
                      election={election}
                      condensed={activeTab === 1}
                      style={{ marginBottom: activeTab === 0 ? '14px' : '6px', marginRight: '14px' }}
                      precedentElection={election.runoffPrecedent ? precedentElectionsMap[election.runoffPrecedent] : null}
                    />
                  ))
                :
                  <LoadingTile>
                    <div style={{ textAlign: 'center', color: '#666' }}>
                      {debouncedSearchTerm ? 'No elections found matching your search.' : 'No elections found.'}
                    </div>
                  </LoadingTile>
            }
          </ElectionTileWrapper>

          {!loading && initialLoad && (
            <PaginationWrapper>
              <Pagination
                count={Math.ceil((activeTab === 0 ? liveElectionsTotal : previousElectionsTotal) / 10)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </PaginationWrapper>
          )}
        </>
      )}
      
      <Snackbar
        open={Boolean(error)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={6000}
        style={{position: 'absolute'}}
        onClose={() => setError(null)}
      >
        <Alert
          severity="error"
          onClose={() => setError(null)}
          elevation={6}
          variant="filled"
        >
          {error?.message ?? `${error}`}
        </Alert>
      </Snackbar>
    </Wrapper>
  )
}

export default Elections;
